import React, { useEffect, useState } from 'react';
import { Activity, Customer, Project, TimeEntry } from '../types/TimeEntry';
import { timeRegistrationService } from '../services/TimeRegistrationService';
import { useAuth } from '../contexts/AuthContext';

export interface CopilotSuggestion {
  customerId: number | null;
  projectId: number | null;
  activityId: number | null;
  hours: number;
  description: string;
}

export type CurrentUser = { Id: number; Title: string; EMail: string; LoginName: string };

export interface TimeCatalog {
  customers: Customer[];
  projects: Project[];
  activities: Activity[];
}

const isIgniaCustomer = (customer: Customer): boolean => /ignia/i.test(customer.Title);

// Same visibility rules as AddEntryModal: active projects (restricted for external users)
// and only customers that have one of those projects
export const loadVisibleCatalog = async (token: string, currentUser: CurrentUser | null): Promise<TimeCatalog> => {
  const user = currentUser ?? await timeRegistrationService.getCurrentUserInfo(token);
  const [activities, customers, projects, allowedProjectIds] = await Promise.all([
    timeRegistrationService.getActivities(token),
    timeRegistrationService.getCustomers(token),
    timeRegistrationService.getProjects(token),
    timeRegistrationService.getAllowedProjectIdsForUser(user.Id, token),
  ]);

  const activeProjects = projects.filter((p) => p.IsActive !== false);
  const allowedProjects = allowedProjectIds.length > 0
    ? activeProjects.filter((p) => allowedProjectIds.includes(p.Id))
    : activeProjects;
  const customerIds = new Set(
    customers.filter((customer) => customer.IsActive !== false && !isIgniaCustomer(customer)).map((customer) => customer.Id)
  );
  const visibleProjects = allowedProjects.filter((project) => project.Customer && customerIds.has(project.Customer.Id));
  const customerIdsWithProjects = new Set(visibleProjects.filter((p) => p.Customer?.Id).map((p) => p.Customer!.Id));

  return {
    activities: activities.filter((a) => a.IsActive !== false),
    customers: customers.filter((c) => c.IsActive !== false && !isIgniaCustomer(c) && customerIdsWithProjects.has(c.Id)),
    projects: visibleProjects,
  };
};

export const filterCustomerTimeSuggestions = (
  suggestions: CopilotSuggestion[],
  catalog: TimeCatalog | null
): CopilotSuggestion[] => {
  if (!catalog) return [];

  const customers = new Map(catalog.customers.map((customer) => [customer.Id, customer]));
  const projects = new Map(catalog.projects.map((project) => [project.Id, project]));
  return suggestions.filter((suggestion) => {
    const customer = suggestion.customerId == null ? undefined : customers.get(suggestion.customerId);
    if (!customer || isIgniaCustomer(customer)) return false;
    if (suggestion.projectId == null) return true;
    return projects.get(suggestion.projectId)?.Customer?.Id === suggestion.customerId;
  });
};

type RowStatus = 'draft' | 'saving' | 'saved' | 'error';

interface Row {
  included: boolean;
  customerId: string;
  projectId: string;
  activityId: string;
  hours: string;
  description: string;
  status: RowStatus;
  error?: string;
}

// Appended to every description when saved, so AI-made entries are visible in the time report until a human adjusts them
const AI_MARKER = '(gjord med AI, justera mänskligt!!)';

// The catalog sent to Copilot has "(id 12)" after each name and Copilot sometimes copies it into its text
export const stripIds = (text: string): string => text.replace(/\s*\(id:?\s*\d+\)/gi, '');

const activityKey = (customerId: string, projectId: string) => `${customerId}:${projectId}`;

// Turns Copilot's ids into form values, dropping ids that aren't in the user's visible lists
const toRow = (suggestion: CopilotSuggestion, catalog: TimeCatalog, saved: boolean): Row => {
  const project = catalog.projects.find((p) => p.Id === suggestion.projectId);
  let customerId = catalog.customers.some((c) => c.Id === suggestion.customerId) ? String(suggestion.customerId) : '';
  let projectId = '';
  if (project && (!customerId || !project.Customer || String(project.Customer.Id) === customerId)) {
    projectId = String(project.Id);
    if (!customerId && project.Customer) customerId = String(project.Customer.Id);
  }

  return {
    included: suggestion.hours > 0,
    customerId,
    projectId,
    activityId: suggestion.activityId != null ? String(suggestion.activityId) : '',
    hours: suggestion.hours > 0 ? String(suggestion.hours) : '',
    description: stripIds(suggestion.description).trim(),
    status: saved ? 'saved' : 'draft',
  };
};

interface CopilotTimeSuggestionsProps {
  suggestions: CopilotSuggestion[];
  savedRows: number[];
  currentUser: CurrentUser | null;
  onRowsSaved: (rowIndexes: number[]) => void;
  onEntriesAdded?: () => void;
}

const CopilotTimeSuggestions: React.FC<CopilotTimeSuggestionsProps> = ({
  suggestions,
  savedRows,
  currentUser,
  onRowsSaved,
  onEntriesAdded,
}) => {
  const { getAccessToken, user } = useAuth();
  const [catalog, setCatalog] = useState<TimeCatalog | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [activitiesByKey, setActivitiesByKey] = useState<Record<string, Activity[]>>({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load the lists and build the rows whenever Copilot delivers new suggestions
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Kunde inte få åtkomsttoken');
        const loaded = await loadVisibleCatalog(token, currentUser);
        if (cancelled) return;
        setCatalog(loaded);
        setRows(suggestions.map((s, i) => toRow(s, loaded, savedRows.includes(i))));
        setLoadError(null);
      } catch (err) {
        console.error('[CopilotSuggestions] Could not load lists:', err);
        if (!cancelled) setLoadError('Kunde inte ladda kunder, projekt och aktiviteter');
      }
    })();
    return () => {
      cancelled = true;
    };
    // savedRows only matters for the initial build - later saves are tracked in the row status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions]);

  // Activities depend on customer (+ project), same as in AddEntryModal - load each combination once
  useEffect(() => {
    const missing = Array.from(new Set(
      rows.filter((r) => r.customerId).map((r) => activityKey(r.customerId, r.projectId))
    )).filter((key) => !(key in activitiesByKey));
    if (missing.length === 0) return;

    (async () => {
      const token = await getAccessToken();
      if (!token) return;
      const loaded = await Promise.all(missing.map(async (key) => {
        const [customerId, projectId] = key.split(':');
        try {
          const list = await timeRegistrationService.getActivitiesForCustomer(
            parseInt(customerId), token, projectId ? parseInt(projectId) : undefined
          );
          return [key, list.filter((a) => a.IsActive !== false)] as const;
        } catch {
          return [key, []] as const;
        }
      }));
      setActivitiesByKey((prev) => ({ ...prev, ...Object.fromEntries(loaded) }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateRow = (index: number, changes: Partial<Row>) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...changes, error: undefined } : row)));

  const handleCustomerChange = (index: number, customerId: string) =>
    updateRow(index, { customerId, projectId: '' });

  const handleProjectChange = (index: number, projectId: string) => {
    const project = catalog?.projects.find((p) => String(p.Id) === projectId);
    updateRow(index, project?.Customer ? { projectId, customerId: String(project.Customer.Id) } : { projectId });
  };

  const getActivityOptions = (row: Row): Activity[] | undefined =>
    row.customerId ? activitiesByKey[activityKey(row.customerId, row.projectId)] : [];

  const validate = (row: Row): string | null => {
    if (!row.customerId) return 'Välj kund';
    if (!getActivityOptions(row)?.some((a) => String(a.Id) === row.activityId)) return 'Välj aktivitet';
    const hours = parseFloat(row.hours.replace(',', '.'));
    if (isNaN(hours) || hours <= 0 || hours > 24) return 'Ange timmar (0,1–24)';
    return null;
  };

  const pendingIndexes = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => row.included && row.status !== 'saved')
    .map(({ i }) => i);

  const totalHours = rows
    .filter((r) => r.included || r.status === 'saved')
    .reduce((sum, r) => sum + (parseFloat(r.hours.replace(',', '.')) || 0), 0);

  const handleSave = async () => {
    if (!catalog) return;

    const invalid = pendingIndexes
      .map((i) => ({ i, error: validate(rows[i]) }))
      .filter((v) => v.error);
    if (invalid.length > 0) {
      setRows((prev) => prev.map((row, i) => {
        const problem = invalid.find((v) => v.i === i);
        return problem ? { ...row, status: 'error', error: problem.error! } : row;
      }));
      return;
    }

    setSaving(true);
    const token = await getAccessToken();
    const date = new Date().toLocaleDateString('sv-SE');
    const savedNow: number[] = [];

    // One at a time so a failing row doesn't hide which ones made it
    for (const i of pendingIndexes) {
      const row = rows[i];
      updateRow(i, { status: 'saving' });
      try {
        if (!token) throw new Error('Kunde inte få åtkomsttoken');
        const customer = catalog.customers.find((c) => String(c.Id) === row.customerId)!;
        const project = catalog.projects.find((p) => String(p.Id) === row.projectId);
        const activity = getActivityOptions(row)!.find((a) => String(a.Id) === row.activityId)!;

        const entry: Omit<TimeEntry, 'Id'> = {
          // "Beskrivning" in the app is the Title field (Details is the longer "kom ihåg" comment)
          Title: `${row.description.trim()} ${AI_MARKER}`.trim(),
          Date: date,
          Hours: parseFloat(row.hours.replace(',', '.')),
          Activity: { Id: activity.Id, Title: activity.Title },
          Customer: { Id: customer.Id, Title: customer.Title },
          Person: currentUser || {
            Id: 0,
            Title: user?.name || user?.username || 'Nuvarande användare',
            EMail: user?.username || '',
            LoginName: user?.username || '',
          },
          ...(project && { Project: { Id: project.Id, Title: project.Title } }),
        };
        await timeRegistrationService.addTimeEntry(entry, token);
        savedNow.push(i);
        updateRow(i, { status: 'saved' });
      } catch (err) {
        console.error('[CopilotSuggestions] Could not add entry:', err);
        setRows((prev) => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'error', error: 'Kunde inte sparas – försök igen' } : r
        ));
      }
    }

    setSaving(false);
    if (savedNow.length > 0) {
      onRowsSaved(savedNow);
      onEntriesAdded?.();
    }
  };

  if (loadError) return <div className="copilot-summary-error">{loadError}</div>;
  if (!catalog) return null;

  const today = new Date().toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="copilot-suggestions">
      <div className="copilot-suggestions-heading">
        <h4>Förslag på tidrapport för {today}</h4>
        <span className="copilot-suggestions-hint">Justera och bocka i det du vill lägga in</span>
      </div>

      <div className="copilot-suggestion-list">
        {rows.map((row, i) => {
          const isSaved = row.status === 'saved';
          const activityOptions = getActivityOptions(row);
          const activityValue = activityOptions?.some((a) => String(a.Id) === row.activityId) ? row.activityId : '';
          const projectOptions = catalog.projects.filter(
            (p) => !row.customerId || !p.Customer || String(p.Customer.Id) === row.customerId
          );

          return (
            <div
              key={i}
              className={`copilot-suggestion${isSaved ? ' is-saved' : ''}${!row.included && !isSaved ? ' is-excluded' : ''}${row.status === 'error' ? ' has-error' : ''}`}
            >
              <label className="copilot-suggestion-check">
                {isSaved ? (
                  <span className="copilot-suggestion-saved" title="Inlagd i tidrapporten">✓</span>
                ) : (
                  <input
                    type="checkbox"
                    checked={row.included}
                    disabled={saving}
                    onChange={(e) => updateRow(i, { included: e.target.checked })}
                    aria-label={`Ta med rad ${i + 1}`}
                  />
                )}
                <span className="copilot-suggestion-number">{i + 1}</span>
              </label>

              <select
                value={row.customerId}
                disabled={isSaved || saving}
                onChange={(e) => handleCustomerChange(i, e.target.value)}
                aria-label="Kund"
              >
                <option value="">Välj kund…</option>
                {catalog.customers.map((c) => <option key={c.Id} value={c.Id}>{c.Title}</option>)}
              </select>

              <select
                value={row.projectId}
                disabled={isSaved || saving}
                onChange={(e) => handleProjectChange(i, e.target.value)}
                aria-label="Projekt"
              >
                <option value="">Inget projekt</option>
                {projectOptions.map((p) => <option key={p.Id} value={p.Id}>{p.Title}</option>)}
              </select>

              <select
                value={activityValue}
                disabled={isSaved || saving || !row.customerId}
                onChange={(e) => updateRow(i, { activityId: e.target.value })}
                aria-label="Aktivitet"
              >
                <option value="">{row.customerId && !activityOptions ? 'Laddar…' : 'Välj aktivitet…'}</option>
                {activityOptions?.map((a) => <option key={a.Id} value={a.Id}>{a.Title}</option>)}
              </select>

              <div className="copilot-suggestion-hours">
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={row.hours}
                  disabled={isSaved || saving}
                  onChange={(e) => updateRow(i, { hours: e.target.value })}
                  aria-label="Timmar"
                />
                <span>h</span>
              </div>

              <input
                className="copilot-suggestion-description"
                type="text"
                value={row.description}
                disabled={isSaved || saving}
                onChange={(e) => updateRow(i, { description: e.target.value })}
                placeholder="Beskrivning"
                aria-label="Beskrivning"
              />

              {row.error && <div className="copilot-suggestion-error">{row.error}</div>}
            </div>
          );
        })}
      </div>

      <div className="copilot-suggestions-footer">
        <span className="copilot-suggestions-total">Totalt {Math.round(totalHours * 100) / 100} h</span>
        <button
          className="copilot-summary-button"
          onClick={handleSave}
          disabled={saving || pendingIndexes.length === 0}
        >
          {saving
            ? 'Lägger in…'
            : pendingIndexes.length === 0
              ? 'Allt inlagt'
              : `Lägg in ${pendingIndexes.length} ${pendingIndexes.length === 1 ? 'rad' : 'rader'} i tidrapporten`}
        </button>
      </div>
    </div>
  );
};

export default CopilotTimeSuggestions;
